// src/hooks/layout/useConstructionSiteLayout.js
//
// Şantiye paftası layout hook'u.
// Şantiye = SALT-OKUNUR TİP PROJE: sabit bir DXF taban çizimi (şantiye-3f.dxf)
// paftaya yüklenir + üzerine BAŞLIK (sağ) ve VAZİYET PLANI (sol) yerleştirilir.
// Editörde çizim ekleme/çıkarma YOK, supply nesnesine DOKUNULMAZ.
//
// DXF'te iki placeholder alan işaretli (kullanıcı verdi):
//   - Vaziyet: x:-833 y:4468 1700×3000 (sol)  — yeşil çerçeve + "vaziyet planı" yazısı
//   - Başlık:  x:9736 y:4468 1715×2415 (sağ)  — yeşil çerçeve + "başlık" yazısı
// Bu iki alan içindeki placeholder entity'leri (KESİT çerçeve + Kapı_Pencere yazı)
// filtrelenir; yerlerine başlık + vaziyet dokümanı ölçeklenerek oturtulur.

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector, useDispatch } from 'react-redux';
import DxfParser from 'dxf-parser';
import {
  getHatchFromDXF, getEntityTransparencyMap, getAttribsFromDXF,
  getLayersFromDXF, getOleFramesFromDXF, dxfDrawCore,
} from '../../modules/dxf';
import { buildSingleDocument } from '../../modules/documents';
import { buildBaslikTemplateGroup } from '../../utils/renderCustomBaslik';
import { store as appStore } from '../../redux/store';
import { selectProjectType } from '../../redux/projectTypeSlice';
import { updateFloorPlan } from '../../redux/floorPlanSlice';

const SANTIYE_DXF_URL = '/santiye/santiye-3f.dxf';
// Şantiye başlığı — Ş+antiye logosu, "ŞANTİYE PROJESİ", sabit 3800W + Projabze OLE.
const SANTIYE_BASLIK_DXF_URL = '/santiye/baslik-santiye.dxf';
const SANTIYE_TAG = 'isSantiyeBase';
const SANTIYE_DOC_TAG = 'isSantiyeDoc';

// Placeholder alanları (kullanıcı AutoCAD koordinatları)
const AREA_VAZIYET = { x: -833, y: 4468, w: 1700, h: 3000 };
const AREA_BASLIK = { x: 9736, y: 4468, w: 1715, h: 2415 };

// Modül seviyesi cache — DXF (1.3MB) tek sefer fetch + parse edilir.
let _cachedParsePromise = null;
// Şantiye başlık DXF ham metni (buildBaslikTemplateGroup parse eder) — tek sefer fetch.
let _baslikDxfPromise = null;
async function loadBaslikDxf() {
  if (_baslikDxfPromise) return _baslikDxfPromise;
  _baslikDxfPromise = (async () => {
    const res = await fetch(SANTIYE_BASLIK_DXF_URL);
    if (!res.ok) throw new Error('şantiye başlık DXF fetch ' + res.status);
    return res.text();
  })().catch(err => { _baslikDxfPromise = null; throw err; });
  return _baslikDxfPromise;
}

// Bir noktanın bir alan içinde olup olmadığı (küçük tolerans).
function inArea(x, y, a, tol = 5) {
  return x >= a.x - tol && x <= a.x + a.w + tol && y >= a.y - tol && y <= a.y + a.h + tol;
}

// Entity'nin temsili noktaları.
function entityPoints(e) {
  const pts = [];
  if (e.vertices) for (const v of e.vertices) if (v && v.x != null) pts.push([v.x, v.y]);
  if (e.position && e.position.x != null) pts.push([e.position.x, e.position.y]);
  if (e.startPoint) pts.push([e.startPoint.x, e.startPoint.y]);
  if (e.endPoint) pts.push([e.endPoint.x, e.endPoint.y]);
  if (e.center && e.center.x != null) pts.push([e.center.x, e.center.y]);
  return pts;
}

// Placeholder layer'ları: çerçeveler KESİT, yazılar Kapı_Pencere (Türkçe/encoding
// varyantlarına dayanıklı normalize ile eşleşir).
function isPlaceholderLayer(e) {
  const n = String(e.layer || '').toLowerCase().replace(/[^a-z_]/g, '');
  return n.startsWith('kes') || n.includes('pencere');
}

// Bu iki alandaki placeholder entity'lerini (KESİT çerçeve + Kapı_Pencere yazı) çıkar.
// Yalnız o layer'larda VE merkezi alan içinde olanlar atılır → diğer KESİT kesit
// kutuları ve plan içeriği korunur.
function stripPlaceholders(entities) {
  return entities.filter(e => {
    if (!isPlaceholderLayer(e)) return true;
    const pts = entityPoints(e);
    if (pts.length === 0) return true;
    const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    if (inArea(cx, cy, AREA_VAZIYET, 40) || inArea(cx, cy, AREA_BASLIK, 40)) return false;
    return true;
  });
}

async function loadSantiyeParsed() {
  if (_cachedParsePromise) return _cachedParsePromise;
  _cachedParsePromise = (async () => {
    const res = await fetch(SANTIYE_DXF_URL);
    if (!res.ok) throw new Error('şantiye DXF fetch ' + res.status);
    const rawText = await res.text();
    const parser = new DxfParser();
    const dxf = parser.parseSync(rawText);
    const dxfLines = rawText.split('\n').map(l => l.trim());

    const hatch = getHatchFromDXF(dxfLines);
    if (Array.isArray(hatch.hatch)) dxf.entities = [...dxf.entities, ...hatch.hatch];
    const tMap = getEntityTransparencyMap(dxfLines);
    dxf.entities = dxf.entities.map(e => {
      const k = e.handle?.toUpperCase();
      return (k && tMap[k] !== undefined) ? { ...e, transparency: tMap[k] } : e;
    });
    const ole = getOleFramesFromDXF(dxfLines);
    if (ole.length) dxf.entities = [...dxf.entities, ...ole];
    const attribs = getAttribsFromDXF(dxfLines);
    dxf.entities = dxf.entities.map(e => {
      if (e.type !== 'INSERT') return e;
      const k = e.handle?.toUpperCase();
      return (k && attribs[k]) ? { ...e, attribs: attribs[k] } : e;
    });

    // Placeholder çerçeve+yazıları (vaziyet/başlık alanları içindekiler) çıkar.
    dxf.entities = stripPlaceholders(dxf.entities);

    const parsedLayers = dxf.tables?.layer?.layers;
    let layerArray = Array.isArray(parsedLayers)
      ? parsedLayers
      : parsedLayers ? Object.values(parsedLayers) : [];
    const enh = getLayersFromDXF(dxfLines);
    layerArray = layerArray.map(l => {
      const e = enh[l.name];
      return e ? { ...l, onOff: e.onOff, locked: e.locked, frozen: e.frozen, lineType: e.lineType, lineWeight: e.lineWeight, transparency: e.transparency ?? 0 } : l;
    });
    return { dxf, layers: layerArray };
  })().catch(err => { _cachedParsePromise = null; throw err; });
  return _cachedParsePromise;
}

// Bir grubu verilen alana ölçekleyip oturtur.
// align: 'top' → grubun üstü alanın üstüne; 'bottom' → grubun altı alanın altına (+ offsetY).
function fitGroupToArea(group, area, { align = 'top', offsetY = 0 } = {}) {
  // Grubun kendi normalizasyonunu (buildBaslikTemplateGroup grup pozisyonuyla
  // normalize eder) sıfırla ki bbox GERÇEK içerik sınırını versin; aksi halde
  // ezilen pozisyon yüzünden başlık DXF-orijinine göre kayıyordu.
  group.position.set(0, 0, 0);
  group.scale.setScalar(1);
  const bbox = new THREE.Box3().setFromObject(group);
  if (bbox.isEmpty()) return;
  const size = bbox.getSize(new THREE.Vector3());
  const gw = size.x || 1, gh = size.y || 1;
  const scale = Math.min(area.w / gw, area.h / gh);
  group.scale.setScalar(scale);
  const posX = area.x - bbox.min.x * scale;
  const posY = align === 'bottom'
    ? (area.y + offsetY) - bbox.min.y * scale   // grubun alt kenarı = alan tabanı + offsetY
    : (area.y + area.h) - bbox.max.y * scale;   // grubun üst kenarı = alan tepesi
  group.position.set(posX, posY, 0);
}

// Şantiye sahnesine başlık (sağ) + vaziyet (sol) dokümanlarını kurar.
async function buildSantiyeDocs(scene, dispatch) {
  // Eski dokümanları temizle
  const stale = [];
  scene.traverse(o => { if (o.userData?.[SANTIYE_DOC_TAG]) stale.push(o); });
  stale.filter(o => o.parent === scene).forEach(o => {
    o.traverse(c => { c.geometry?.dispose?.(); if (c.material) (Array.isArray(c.material) ? c.material : [c.material]).forEach(m => { m.map?.dispose?.(); m.dispose?.(); }); });
    scene.remove(o);
  });

  const st = appStore.getState();
  const projectType = selectProjectType(st);
  const sitePlan = st.utils?.sitePlan;
  const buildParams = {
    simpleSupply: st.simpleSupply, floorPlans: [], projectType,
    mainGrounding: null, projectMeshSize: null, dispatch, updateFloorPlan,
    FLOOR_PLAN_TAB: 'elektrik', energyRoomConfirmed: false, erData: {},
    panoDetayConfig: null, panelTypes: st.panelConfig?.panelTypes, appStore,
  };

  const tag = (g) => { g.traverse(c => { c.userData = c.userData || {}; c.userData[SANTIYE_DOC_TAG] = true; }); g.userData = g.userData || {}; g.userData[SANTIYE_DOC_TAG] = true; };

  // Başlık (sağ) — ŞANTİYE başlık DXF'i (Ş+antiye, "ŞANTİYE PROJESİ", sabit 3800W,
  // Projabze OLE). Standart app-başlığı yerine bu DXF şablon olarak render edilir.
  try {
    const dxfRawText = await loadBaslikDxf();
    const group = buildBaslikTemplateGroup({ id: 'santiye-baslik', dxfRawText, bindings: {} }, appStore.getState());
    if (group && group.children.length) {
      fitGroupToArea(group, AREA_BASLIK);
      tag(group);
      scene.add(group);
    }
  } catch (err) { console.error('[santiye] başlık kurulamadı:', err); }

  // Vaziyet planı (sol) — mimaride seçildiyse
  if (sitePlan?.width && sitePlan?.height && sitePlan?.group) {
    try {
      const def = { name: 'Vaziyet Planı', w: sitePlan.width, h: sitePlan.height, hasFrame: true, sitePlanId: sitePlan.id, objectsIds: sitePlan.objectsIds, group: sitePlan.group };
      const { documentGroup } = buildSingleDocument(def, buildParams);
      if (documentGroup && documentGroup.children.length) {
        fitGroupToArea(documentGroup, AREA_VAZIYET, { align: 'bottom', offsetY: 50 });
        tag(documentGroup);
        scene.add(documentGroup);
      }
    } catch (err) { console.error('[santiye] vaziyet kurulamadı:', err); }
  }
}

// DXF yüklendikten sonra kamerayı sahne içeriğine ortalar.
function fitCameraToScene(scene, camera, controls, container) {
  if (!camera || !controls || !container) return;
  const box = new THREE.Box3();
  let hasContent = false;
  scene.traverse(child => {
    if (child.isMesh || child.isLine || child.isLineSegments) {
      box.expandByObject(child);
      hasContent = true;
    }
  });
  if (!hasContent || box.isEmpty()) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  camera.position.set(center.x, center.y + 1000, 10);
  controls.target.set(center.x, center.y + 1000, 0);
  const contentAspect = size.x / (size.y || 1);
  const canvasAspect = container.clientWidth / container.clientHeight;
  const fitZoom = contentAspect > canvasAspect
    ? container.clientWidth / size.x
    : container.clientHeight / size.y;
  camera.zoom = fitZoom * 0.9;
  camera.updateProjectionMatrix();
  controls.update?.();
}

export const useConstructionSiteLayout = (scenesRef, sceneTabId, viewRefs = {}) => {
  const TARGET_TAB = sceneTabId || 'santiye';
  const allGroupsRef = useRef([]);
  const boundsRef = useRef({});
  const documentBoundsRef = useRef([]);
  const dxfLoadedRef = useRef(false);
  const dispatch = useDispatch();
  // viewRefs (camera, controlsRef, canvasRef) her render'da güncellenir → stale closure olmaz.
  const viewRefsRef = useRef(viewRefs);
  useEffect(() => { viewRefsRef.current = viewRefs; });

  const santiyeSignal = useSelector(s => {
    const enabled = s.projectTabs?.enabledPaftaIds || [];
    const active = s.projectTabs?.activeTabId;
    const on = enabled.includes('santiye') || enabled.includes('construction_site');
    return `${on ? 1 : 0}|${active}`;
  });
  const updateCounter = useSelector(s => s.utils?.updateCounter);

  useEffect(() => {
    const scene = scenesRef.current?.[TARGET_TAB];
    if (!scene) return;

    // 1) DXF tabanı — bir kez yükle
    if (!dxfLoadedRef.current) {
      dxfLoadedRef.current = true;
      let cancelled = false;
      loadSantiyeParsed().then(({ dxf, layers }) => {
        if (cancelled) return;
        const s = scenesRef.current?.[TARGET_TAB];
        if (!s) { dxfLoadedRef.current = false; return; }
        // eski taban temizle
        const stale = [];
        s.traverse(o => { if (o.userData?.[SANTIYE_TAG]) stale.push(o); });
        stale.filter(o => o.parent === s).forEach(o => {
          o.traverse(c => { c.geometry?.dispose?.(); if (c.material) (Array.isArray(c.material) ? c.material : [c.material]).forEach(m => { m.map?.dispose?.(); m.dispose?.(); }); });
          s.remove(o);
        });
        const group = dxfDrawCore(new THREE.Group(), dxf.entities, dxf.blocks || {}, 'group', layers);
        if (group) {
          group.userData = group.userData || {};
          group.userData[SANTIYE_TAG] = true;
          group.traverse(c => { c.userData = c.userData || {}; c.userData[SANTIYE_TAG] = true; });
          s.add(group);
          allGroupsRef.current = [group];
        }
        // DXF geldikten sonra başlık + vaziyeti kur (başlık DXF async fetch)
        buildSantiyeDocs(s, dispatch).catch(e => console.error('[santiye] docs:', e));
        // Kamerayı sahne içeriğine ortala (yalnız ilk yüklemede — DXF tabanına göre)
        const { camera, controlsRef, canvasRef } = viewRefsRef.current;
        fitCameraToScene(s, camera, controlsRef?.current, canvasRef?.current);
      }).catch(err => { console.error('[santiye] DXF yüklenemedi:', err); dxfLoadedRef.current = false; });
      return () => { cancelled = true; };
    }

    // 2) DXF zaten var — veri değişince (updateCounter) başlık + vaziyeti yeniden kur
    buildSantiyeDocs(scene, dispatch).catch(e => console.error('[santiye] docs:', e));
  }, [santiyeSignal, updateCounter, scenesRef, TARGET_TAB, dispatch]);

  return { allGroupsRef, boundsRef, documentBoundsRef };
};
